import { useEffect, useRef, useState } from "react";

// Input types that summon the on-screen keyboard. Pickers (date, color, file)
// and toggles (checkbox, radio, range) do not, so they are left out.
const KEYBOARD_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "tel",
  "url",
  "number",
  "password",
]);

export function isTextEntry(el) {
  if (!el || el.disabled || el.readOnly) return false;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName === "INPUT") {
    return KEYBOARD_INPUT_TYPES.has(String(el.type || "text").toLowerCase());
  }
  return el.isContentEditable === true;
}

// The visual viewport being shorter than the layout viewport by more than this
// means a keyboard is covering it. Browser chrome (URL bar) moves far less.
const KEYBOARD_MIN_PX = 120;

function viewportCoveredByKeyboard() {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (!vv || !vv.height) return false;
  // Normalise for pinch-zoom: a zoomed-in page also has a short visual
  // viewport, but that is not a keyboard.
  const visible = vv.height * (vv.scale || 1);
  return window.innerHeight - visible > KEYBOARD_MIN_PX;
}

function coarsePointer() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

function keyboardOpenNow() {
  if (typeof document === "undefined") return false;
  if (viewportCoveredByKeyboard()) return true;
  return coarsePointer() && isTextEntry(document.activeElement);
}

// iOS (Safari and installed PWAs) re-anchors position:fixed elements to the
// keyboard-shrunk visual viewport and then leaves them there after the
// keyboard goes away, until the page next scrolls. A zero-distance scroll is
// enough to make WebKit re-sync the layout viewport and snap them back.
function reanchorFixedElements() {
  if (typeof window === "undefined" || typeof window.scrollTo !== "function") return;
  const x = window.scrollX || 0;
  const y = window.scrollY || 0;
  window.scrollTo(x, y + 1);
  window.scrollTo(x, y);
}

/**
 * True while the on-screen keyboard is (very likely) covering the bottom of
 * the viewport: the visual viewport has shrunk well past what browser chrome
 * accounts for, or a text-entry field is focused on a touch device.
 *
 * Fixed bottom chrome (the mobile bottom nav, floating action buttons) should
 * hide while this is true. Sitting above the keyboard is useless, and on iOS
 * it is also where the bug lives: the element stays pinned to that mid-screen
 * position after the keyboard is dismissed, scrolling with the page instead
 * of the viewport. When the keyboard closes this hook also nudges the window
 * so anything still fixed snaps back to the real bottom edge.
 */
export default function useVirtualKeyboard() {
  const [open, setOpen] = useState(keyboardOpenNow);
  const wasOpen = useRef(open);

  useEffect(() => {
    const vv = window.visualViewport;
    let frame = 0;
    const update = () => {
      // Read after the browser has applied the focus/resize, not during it:
      // inside focusout, activeElement is still the element being left.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setOpen(keyboardOpenNow()));
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    if (vv) {
      vv.addEventListener("resize", update);
      vv.addEventListener("scroll", update);
    }
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
      if (vv) {
        vv.removeEventListener("resize", update);
        vv.removeEventListener("scroll", update);
      }
    };
  }, []);

  useEffect(() => {
    if (wasOpen.current && !open) reanchorFixedElements();
    wasOpen.current = open;
  }, [open]);

  return open;
}
